/**
 * Shared error/not-found state for detail pages.
 *
 * Hierarchical "back/up" navigation is owned entirely by the topbar
 * breadcrumb (journey trail on md+, compact "‹ parent" below md) — detail
 * pages render no back chrome of their own. DetailError is the one
 * exception-adjacent affordance: when an entity fails to load, the crumb
 * tail degrades to a truncated id, so the error card itself offers an
 * explicit route back to the parent list.
 */
import { Link } from '@tanstack/react-router';
import { ArrowLeft, SearchX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty';
import { ErrorBanner } from '@/components/error-banner';
import { ApiError } from '@/lib/api';

export interface DetailErrorProps {
  /** The query error (ApiError for HTTP failures). */
  error: unknown;
  /** Human name of the entity kind, e.g. "audit", "brief". */
  entityKind: string;
  /** Canonical parent list. */
  backTo: string;
  backLabel: string;
}

/**
 * Error state for a detail page that failed to load. 404/403 get a
 * friendly not-found treatment; other errors keep the standard banner.
 * Both always include a button back to the parent list.
 */
export function DetailError({
  error,
  entityKind,
  backTo,
  backLabel,
}: DetailErrorProps): JSX.Element {
  const status = error instanceof ApiError ? error.status : null;
  const backButton = (
    <Button asChild variant="outline" size="sm">
      <Link to={backTo as string}>
        <ArrowLeft className="h-4 w-4" />
        Back to {backLabel}
      </Link>
    </Button>
  );

  if (status === 404 || status === 403) {
    return (
      <EmptyState
        icon={SearchX}
        title={`This ${entityKind} isn't available`}
        description={
          status === 404
            ? `It may have been deleted, or the link is wrong.`
            : `Your role in this workspace doesn't include access to it.`
        }
        action={backButton}
      />
    );
  }

  return (
    <div className="space-y-4">
      <ErrorBanner error={error} />
      {backButton}
    </div>
  );
}
